import {
  useDialogStore,
  type ConfirmOpts, type AlertOpts, type PromptOpts,
  type NormalizedDialog, type DialogValue,
} from '../stores/dialogStore'

export type { DialogTone, ConfirmOpts, AlertOpts, PromptOpts } from '../stores/dialogStore'

const asObj = <T extends { message?: string }>(opts: string | T): T =>
  (typeof opts === 'string' ? ({ message: opts } as T) : opts)

function normalizeConfirm(opts: string | ConfirmOpts): NormalizedDialog {
  const o = asObj(opts)
  return {
    title: o.title, message: o.message, tone: o.tone ?? 'default',
    confirmLabel: o.confirmLabel ?? 'Confirm', cancelLabel: o.cancelLabel ?? 'Cancel',
    isPrompt: false, defaultValue: '', placeholder: undefined, inputType: 'text', required: false, hasCancel: true,
  }
}
function normalizeAlert(opts: string | AlertOpts): NormalizedDialog {
  const o = asObj(opts)
  return {
    title: o.title, message: o.message, tone: o.tone ?? 'default',
    confirmLabel: o.confirmLabel ?? 'OK', cancelLabel: 'Cancel',
    isPrompt: false, defaultValue: '', placeholder: undefined, inputType: 'text', required: false, hasCancel: false,
  }
}
function normalizePrompt(opts: string | PromptOpts): NormalizedDialog {
  const o = asObj(opts)
  return {
    title: o.title, message: o.message, tone: o.tone ?? 'default',
    confirmLabel: o.confirmLabel ?? 'OK', cancelLabel: o.cancelLabel ?? 'Cancel',
    isPrompt: true, defaultValue: o.defaultValue ?? '', placeholder: o.placeholder,
    inputType: o.inputType ?? 'text', required: o.required ?? false, hasCancel: true,
  }
}

export const dialog = {
  confirm(opts: string | ConfirmOpts): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      useDialogStore.getState().enqueue({ kind: 'confirm', options: normalizeConfirm(opts), resolve: resolve as (v: DialogValue) => void })
    })
  },
  alert(opts: string | AlertOpts): Promise<void> {
    return new Promise<void>((resolve) => {
      useDialogStore.getState().enqueue({ kind: 'alert', options: normalizeAlert(opts), resolve: () => resolve() })
    })
  },
  prompt(opts: string | PromptOpts): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      useDialogStore.getState().enqueue({ kind: 'prompt', options: normalizePrompt(opts), resolve: resolve as (v: DialogValue) => void })
    })
  },
}

// Flat aliases for sites that prefer named imports.
export const confirmDialog = dialog.confirm
export const alertDialog = dialog.alert
export const promptDialog = dialog.prompt
